// #1220 — Case-ID Display-Layer (OMS- / Mail- Prefix).
// DB-Werte unveraendert; Helper rendert "OMS-#175" fuer source='oms' und "Mail-#175" sonst.
// #1482 — dritte Quelle: source/display_source='voice_phone' → "Hotline-#175".
// OMS-Sync outbound zu Paul bleibt unangetastet (rohe Nummer).

function casePrefix(source) {
  if (source === 'oms') return 'OMS';
  if (source === 'voice_phone') return 'Hotline';
  return 'Mail';
}

export function formatCaseId(caseId, source) {
  if (caseId == null || caseId === '') return '';
  return `${casePrefix(source)}-#${caseId}`;
}

export function formatCaseIdShort(caseId, source) {
  if (caseId == null || caseId === '') return '';
  return `${casePrefix(source)}-${caseId}`;
}

// Parser fuer Such-Eingaben: erkennt "OMS-#175" / "Mail-#175" / "175"
// Rueckgabe: { number, sourceFilter } wobei sourceFilter 'oms' | 'non_oms' | null
export function parseCaseIdQuery(q) {
  if (!q) return null;
  const trimmed = String(q).trim();
  const omsMatch = trimmed.match(/^oms[-\s#]+(\d+)$/i);
  if (omsMatch) return { number: Number(omsMatch[1]), sourceFilter: 'oms' };
  const mailMatch = trimmed.match(/^mail[-\s#]+(\d+)$/i);
  if (mailMatch) return { number: Number(mailMatch[1]), sourceFilter: 'non_oms' };
  // #1801-Fix — dritte Anzeige-Quelle (#1482): "Hotline-#175" = lokaler Voice-Case.
  const hotlineMatch = trimmed.match(/^hotline[-\s#]+(\d+)$/i);
  if (hotlineMatch) return { number: Number(hotlineMatch[1]), sourceFilter: 'non_oms' };
  return null;
}

// #1501+Customer-Care — Explizite-Ticketnummer-Erkennung: "#794" (führendes # PFLICHT),
// "OMS-#794"/"OMS-794", "Mail-#794"/"Mail-794". Liefert {number, sourceFilter} NUR wenn die
// GANZE Query eine explizite Ticketnummer ist (keine zusätzlichen Wörter). Dann gilt
// Exakt-Modus (id/odoo_case_id === N), kein Partial-includes, kein Text-Fall-through.
// Customer-Care-Verschärfung: eine BARE Zahl ohne # ("524") ist KEINE Ticketnummer mehr
// → fällt durch in die normale Volltext-/Token-Suche (Fließtext-Zahlen wie "524-O-…"
// bleiben findbar). Nur # bzw. OMS-/Mail-Prefix signalisiert die Exakt-ID-Suche.
export function parseTicketNumberQuery(q) {
  if (!q) return null;
  const trimmed = String(q).trim();
  const prefixed = parseCaseIdQuery(trimmed);
  if (prefixed) return prefixed;
  const hashed = trimmed.match(/^#(\d{1,6})$/);
  if (hashed) return { number: Number(hashed[1]), sourceFilter: null };
  return null;
}

// #1362 QW3 — Umlaut-/Akzent-tolerante Normalisierung: lowercase + NFD + Diakritika-Strip,
// damit "muller" ↔ "Müller" matcht. Wird auf Suchbegriff UND Feldwert angewandt.
function normText(s) {
  return String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// #1362 QW1 — Formatierung von Nummern normalisieren (Leerzeichen/Bindestriche raus),
// damit "123 456 789" / "123-456-789" / "123456789" denselben Treffer liefern.
function normDigits(s) {
  return String(s).replace(/[\s-]+/g, '');
}

// #1362 QW5 — EINZIGE Quelle der Cases-Such-Logik (vorher 2 Kopien in Cases.jsx: filtered + searchStats).
// Multi-Field-Suche mit Multi-Word AND + ID-Heuristik (#1112), Prefix-Tokens (#1220).
// Liefert null bei leerer Query, sonst (task) => boolean.
export function buildCaseSearchMatcher(rawQuery) {
  const trimmed = String(rawQuery || '').trim();
  if (!trimmed) return null;
  // #1501 — Query ist eine reine Ticketnummer (#794 / 794 / OMS-#794) → Exakt-Modus:
  // NUR id/odoo_case_id === N, kein Partial-includes, kein Text-Fall-through.
  // Verhindert, dass "#794" auch #795/#664/#42/… (Substring/Fuzzy) liefert.
  const ticketNum = parseTicketNumberQuery(trimmed);
  if (ticketNum) {
    const { number, sourceFilter } = ticketNum;
    return (t) => {
      const isOms = (t.source === 'oms');
      if (sourceFilter === 'oms' && !isOms) return false;
      if (sourceFilter === 'non_oms' && isOms) return false;
      return t.id === number || t.odoo_case_id === number;
    };
  }
  // '#79' → '79' (Hash-Prefix strippen, wie bisher)
  let cleaned = trimmed.replace(/(^|\s)#(\d+)/g, '$1$2');
  // #1362 QW1 — Ziffern-Gruppen, die nur durch Leerzeichen/Bindestriche getrennt sind,
  // zu EINEM Token zusammenziehen ("123 456 789" → "123456789").
  cleaned = cleaned.replace(/\d(?:[ -]+\d+)+/g, m => m.replace(/[ -]+/g, ''));
  const tokens = normText(cleaned).split(/\s+/).filter(Boolean);
  if (!tokens.length) return null;

  const matchToken = (tok, t) => {
    // #1220 — Prefix-Token: "oms-#175", "oms-175", "mail-#175", "mail-175"
    const prefixed = parseCaseIdQuery(tok);
    if (prefixed) {
      const isOms = (t.source === 'oms');
      if (prefixed.sourceFilter === 'oms' && !isOms) return false;
      if (prefixed.sourceFilter === 'non_oms' && isOms) return false;
      if (t.id === prefixed.number) return true;
      if (t.odoo_case_id === prefixed.number) return true;
      return false;
    }
    // #1501-FINAL (FALL 2) — Customer-Care: eine BARE Zahl ohne # ("524") ist KEINE
    // Ticketnummer, sondern ein Volltext-Suchwort. Früher matchte ein 1–6-stelliges
    // Token zusätzlich exakt + per Substring gegen id/odoo_case_id (#1112-ID-Heuristik)
    // → "524" zog auch Case-ID 524 herein, obwohl das BE bei bare Zahlen längst reinen
    // Volltext fährt (kein c.id-Query). Die ID-Heuristik wird ausschließlich noch über die
    // EXPLIZITEN Ticketnummer-Formen (führendes # / OMS-# / Mail-#) ausgelöst:
    // parseTicketNumberQuery (Exakt-Modus oben) bzw. parseCaseIdQuery (prefixed-Token).
    // Bare Ziffern fallen hier bewusst in die Textfeld-Suche durch (gespiegelt am BE-Token-
    // Pfad), damit Fließtext-Zahlen wie "524-O-…" findbar bleiben, aber Case-ID 524 NICHT.
    if (/^\d{7,}$/.test(tok)) {
      // Lange Zahl (z.B. Tracking-Nr) — KEIN ID-Match.
      // #1362 QW1 — gegen Betreff (name) UND Body (search_text) matchen (vorher nur Body),
      // formatierungs-normalisiert (Leerzeichen/Bindestriche im Zielfeld ignorieren).
      return normDigits(`${t.name || ''} ${t.search_text || ''}`).includes(tok);
    }
    const fields = [
      t.name, t.search_text, t.company_name, t.partner_name,
      t.kunde_email, t.category_name,
      // #1362 QW2 — angezeigte Namen auch suchbar: Absender (#1350), Kontakt, Zuständige
      t.absender_name, t.contact_name,
      ...(t.assigned_users || []).map(x => x?.name),
      ...(t.contacts || []).map(x => x?.name),
      ...(t.local_tags || []).map(x => x?.name),
      ...(t.tags || []).map(x => x?.name),
    ];
    return fields.some(f => f && normText(f).includes(tok));
  };

  return (t) => tokens.every(tok => matchToken(tok, t));
}

// #1801 — Per-Kategorie-Matcher für die kategorisierte Suche (Filter-Chips).
// Der Server liefert lokale Treffer (/api/cases/search/categorized); dieser Matcher
// deckt die OMS-Pool-Seite ab (Listen-Shape, keine Bodies → email/inhalt matchen
// client-seitig bewusst NICHT — lokale Treffer kommen vom Server).
// matchIdSets: { bearbeiter: Set<number> } — Dual-Namespace users.id/oms_user_id (#1250/#1780),
// vom Aufrufer aus internalUsers aufgelöst.
// #1801-Fix Teil 2 (Regel 2.1) — Case-Nr ist filter-immun: case_nr-Chips immer,
// default-Chips nur bei EXPLIZITER Ticketnummer ("#4"/"Mail-#4"/"OMS-#4"/"Hotline-#4",
// #1501-Semantik: nackte Zahl ist KEINE Ticketnummer). Muss der Server-Logik in
// routes/cases.js (filterImmune) entsprechen.
export function isFilterImmuneChip(chip) {
  if (!chip) return false;
  if (chip.cat === 'case_nr') return true;
  return chip.cat === 'default' && !!parseTicketNumberQuery(String(chip.val ?? ''));
}

export function buildChipMatcher(chip, matchIdSets = {}) {
  const val = String(chip.val ?? '').trim();
  if (!val) return () => false;
  const nval = normText(val);
  switch (chip.cat) {
    case 'case_nr': {
      // #1801-Fix Teil 2 — Anzeige-Formate ("Mail-#4"/"OMS-#4"/"Hotline-#4"/"#4")
      // quellen-bewusst parsen; nackte Ziffern matchen wie bisher beide Namensräume.
      const tk = parseTicketNumberQuery(val)
        || (/^\d{1,7}$/.test(val.replace(/^#/, '')) ? { number: Number(val.replace(/^#/, '')), sourceFilter: null } : null);
      if (!tk) return () => false;
      return (t) => {
        if (tk.sourceFilter === 'oms' && t.source !== 'oms') return false;
        if (tk.sourceFilter === 'non_oms' && t.source === 'oms') return false;
        return t.id === tk.number || t.odoo_case_id === tk.number;
      };
    }
    case 'betreff':
      return (t) => normText(t.name || '').includes(nval);
    case 'kunde':
      // Chip trägt bei Auswahl aus dem Dropdown eine lokale kunden.id (vtype 'id') —
      // die ist im OMS-Namensraum bedeutungslos → IMMER über das Label (Kundenname) matchen.
      return (t) => {
        const label = chip.vtype === 'id' ? normText(chip.label || '') : nval;
        if (!label) return false;
        return normText(t.company_name || t.partner_name || '').includes(label);
      };
    case 'tracking': {
      const digits = normDigits(val);
      return (t) => normDigits(`${t.name || ''} ${t.search_text || ''}`).includes(digits);
    }
    case 'bearbeiter': {
      const ids = matchIdSets.bearbeiter || new Set([Number(val)]);
      return (t) => (t.assigned_users || []).some(au => ids.has(Number(au.id)));
    }
    case 'tag': {
      if (chip.vtype === 'id') {
        const tid = String(val);
        return (t) => [...(t.local_tags || []), ...(t.tags || [])].some(tg => String(tg.id) === tid);
      }
      return (t) => [...(t.local_tags || []), ...(t.tags || [])].some(tg => normText(tg.name || '').includes(nval));
    }
    case 'email':
    case 'inhalt':
      // Bodies/Adressen sind im OMS-Listen-Shape nicht enthalten → kein Client-Match
      // (lokale Treffer liefert der Server; OMS-Lücke im Handbuch dokumentiert).
      return () => false;
    case 'default':
    default: {
      // #1801-Fix — explizite Ticketnummern ("Mail-#4" etc.) auch im Default-Chip.
      const tk = parseTicketNumberQuery(val);
      const n = tk ? tk.number : (/^#?\d{1,7}$/.test(val) ? Number(val.replace(/^#/, '')) : null);
      const srcOk = (t) => !tk
        || (tk.sourceFilter === 'oms' ? t.source === 'oms' : tk.sourceFilter === 'non_oms' ? t.source !== 'oms' : true);
      return (t) => (n !== null && srcOk(t) && (t.id === n || t.odoo_case_id === n))
        || normText(t.name || '').includes(nval)
        || normText(t.company_name || t.partner_name || '').includes(nval);
    }
  }
}
